import { memo, useCallback, type CSSProperties } from 'react';
import Quill from '@ui/quill/quill';
import styles from './quill-text.module.css';
import type { TextWidgetProps } from './widget-types';
import { useCallbackRef } from '@/utils/use-callback-ref';
import type { QuillColorPanelProps } from '@ui/quill/quill.types';
import QuillColorPanel from './quill-color-panel/quill-color-panel';

export type HeaderWidgetProps = TextWidgetProps;

// defined once, so every render hands Quill the same function
const renderColorPanel = (props: QuillColorPanelProps) => <QuillColorPanel {...props} />;

function HeaderWidget({
  blockId,
  value,
  setValue,
  color,
  fontFamily,
  fontSize = '40px',
  fontWeight = 600,
  lineHeight,
  letterSpacing,
  textAlign,
  textTransform,
  fontStyle,
  whiteSpace,
  background,
  border,
  borderStyle,
  borderColor,
  borderRadius,
  padding = '16px 32px',
  margin,
  fitContent = false,
  align = 'center',
  opacity,
  height,
  boxShadow,
  blockBackground,
  blockBoxShadow,
}: HeaderWidgetProps) {
  const setValueRef = useCallbackRef(setValue);
  const onBlurChanged = useCallback((html?: string) => setValueRef.current?.(html ?? ''), [setValueRef]);
  const boxStyle: CSSProperties = {
    background,
    border: border ? `${border} ${borderStyle ?? 'solid'} ${borderColor ?? 'transparent'}` : undefined,
    borderRadius,
    padding,
    boxShadow,
  };
  const textStyle: CSSProperties = {
    color,
    fontFamily,
    fontSize,
    fontWeight,
    lineHeight,
    letterSpacing,
    textAlign,
    textTransform,
    fontStyle,
    whiteSpace,
    opacity,
    wordWrap: 'break-word',
  };
  // the editor inherits its size from this element rather than carrying a size format, so the
  // toolbar has nothing to show as the current size - this names it for the picker's unset entry
  // without writing anything into the saved html. Quoted, because it lands in a CSS `content`.
  const quillStyle = { ...textStyle, '--quill-default-size': JSON.stringify(fontSize) } as CSSProperties;

  return (
    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0} style={{ tableLayout: 'fixed' }}>
      <tbody>
        <tr>
          <td
            align={fitContent ? align : undefined}
            valign={height ? 'middle' : undefined}
            style={{
              padding: margin,
              textAlign: fitContent ? align : undefined,
              height,
              background: blockBackground,
              boxShadow: blockBoxShadow,
            }}
          >
            <table
              width={fitContent ? undefined : '100%'}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{
                margin: fitContent && align === 'center' ? '0 auto' : undefined,
                // table-layout: auto sizes columns to fit unbreakable content (e.g. one long word)
                // even past the specified width; fixed forces the browser to respect it instead
                tableLayout: fitContent ? undefined : 'fixed',
              }}
            >
              <tbody>
                <tr>
                  <td data-block-id={blockId} style={boxStyle}>
                    <div style={quillStyle} className={styles.quillText}>
                      <Quill
                        html={value ?? ''}
                        onBlurChanged={onBlurChanged}
                        disabled={!setValueRef.current}
                        disableAnchorTargets
                        inheritFontSizes
                        maxTopOffset={202}
                        renderColorPanel={renderColorPanel}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
}
export default memo(HeaderWidget);
